type SentryLikeException = {
    type?: unknown;
    value?: unknown;
};

type SentryLikeEvent = {
    message?: unknown;
    exception?: {
        values?: SentryLikeException[];
    };
};

type SentryLikeHint = {
    originalException?: unknown;
    syntheticException?: unknown;
};

const IGNORED_ERROR_PATTERNS = [
    /Object Not Found Matching Id/i,
    /ResizeObserver loop/i,
    // VivoBrowser / OEM WebView injected scripts (see Sentry P3M-FRONTEND-1Z).
    /processRandomSelector/i,
    /^Script error\.?$/i,
];

/** Cross-origin or browser-injected throws often surface as `error: null`. */
export const isNullishBrowserError = (
    error: unknown,
    message?: unknown
): boolean => {
    if (error != null) return false;

    const msg =
        typeof message === "string"
            ? message.trim()
            : message == null
              ? ""
              : String(message).trim();

    return (
        !msg ||
        msg === "null" ||
        msg === "undefined" ||
        /^Script error\.?$/i.test(msg)
    );
};

const MAX_EXTRACTION_DEPTH = 2;

const addText = (
    value: unknown,
    texts: string[],
    seen: WeakSet<object>,
    depth = 0
) => {
    if (value == null || depth > MAX_EXTRACTION_DEPTH) return;

    if (typeof value === "string") {
        texts.push(value);
        return;
    }

    if (typeof value === "number" || typeof value === "boolean") {
        texts.push(String(value));
        return;
    }

    if (value instanceof Error) {
        texts.push(value.name, value.message);
        if (value.stack) texts.push(value.stack);
        return;
    }

    if (typeof value !== "object") return;
    if (seen.has(value)) return;
    seen.add(value);

    const stringified = String(value);
    if (stringified !== "[object Object]") {
        texts.push(stringified);
    }

    try {
        texts.push(JSON.stringify(value));
    } catch {
        // Circular extension payloads are common; keyed extraction below is enough.
    }

    for (const key of ["message", "value", "reason", "error", "detail", "description"]) {
        addText((value as Record<string, unknown>)[key], texts, seen, depth + 1);
    }
};

export const isIgnoredBrowserNoise = (
    value: unknown,
    message?: unknown
): boolean => {
    if (isNullishBrowserError(value, message)) return true;

    const texts: string[] = [];
    addText(value, texts, new WeakSet<object>());
    addText(message, texts, new WeakSet<object>());
    return texts.some((text) =>
        IGNORED_ERROR_PATTERNS.some((pattern) => pattern.test(text))
    );
};

export const isIgnoredSentryEvent = (
    event?: SentryLikeEvent | null,
    hint?: SentryLikeHint | null
): boolean => {
    const texts: string[] = [];
    const seen = new WeakSet<object>();

    addText(hint?.originalException, texts, seen);
    addText(hint?.syntheticException, texts, seen);
    addText(event?.message, texts, seen);

    for (const exception of event?.exception?.values || []) {
        addText(exception.type, texts, seen);
        addText(exception.value, texts, seen);
    }

    if (
        isNullishBrowserError(
            hint?.originalException,
            event?.exception?.values?.[0]?.value ?? event?.message
        )
    ) {
        return true;
    }

    return texts.some((text) =>
        IGNORED_ERROR_PATTERNS.some((pattern) => pattern.test(text))
    );
};

export const installIgnoredPromiseRejectionSuppressor = () => {
    window.addEventListener(
        "unhandledrejection",
        (event) => {
            if (!isIgnoredBrowserNoise(event.reason, event.type)) return;

            event.preventDefault();
            event.stopImmediatePropagation();
        },
        true
    );
};
